





































































































































import { Component, Prop, Watch } from 'vue-property-decorator';
import CommonVue from '../models/commonVue';
import { BusEvent } from '../models/bus';

import Exercise from '../api/exercise';
import LockManager from '../models/lock';
import Media from '../api/media';

import FieldListeningItem from '../components/FieldListeningItem.vue';
import EditIcon from '../components/icons/EditIcon.vue';
import FieldInternational from '@/components/FieldInternational.vue';
import CopyExercise from '@/components/CopyExercise.vue';
import Module from '@/api/module';
import { checkContent } from '@/api/content-check';
import { isEmpty } from 'lodash';

@Component({
  components: {
    'field-listening-item': FieldListeningItem,
    'edit-icon': EditIcon,
    'field-international': FieldInternational,
    'copy-exercise': CopyExercise
  }
})
export default class EditExerciseAudioText extends CommonVue {
  @Prop({ type: Number }) public moduleId!: number;
  @Prop({ type: Number }) public lessonId!: number;
  @Prop({ type: Exercise }) public exercise!: Exercise;
  @Prop({ type: String, default: Module.SLUG }) public parent!: string;

  public async beforeMount() {
    await this.load();
  }

  @Watch('exercise')
  public async load() {
    this.busy = true;
    this.error = false;

    if (this.exercise && this.exercise.id) {
      this.lockManager = new LockManager(this.EXERCISE_SLUG, this.exercise.id);
      await this.lockManager.checkStatus();
      if (this.lockManager.isBusy) {
        this.$bus.$emit(
          BusEvent.ERROR,
          'Someone else is making changes.',
          new Error(
            'Click refresh to check if they have finished their changes'
          )
        );
      }
    } else {
      this.lockManager = null;
    }

    this.busy = false;
  }

  // enter edit mode if it can get the lock
  @Watch('exercise', { deep: true })
  public async enterEditMode() {
    await this.tryToLock();
  }

  public async deleteClicked(): Promise<void> {
    if (!this.exercise || !this.exercise.id) {
      return;
    }

    this.busy = true;
    this.exercise.isDirty = false;

    try {
      if (!this.lockManager) {
        this.lockManager = new LockManager(
          this.EXERCISE_SLUG,
          this.exercise.id
        );
      }

      await this.tryToLock();

      await this.exercise.delete();

      await this.lockManager.releaseLock();

      this.busy = false;
      this.$router.push(
        `/${this.parent}/${this.moduleId}/${this.LESSON_SLUG}/${this.lessonId}/${this.EXERCISE_SLUG}`
      );
    } catch (e) {
      this.busy = false;
      this.$bus.$emit(BusEvent.ERROR, 'Could not delete exercise', e);
    }
  }

  public async saveClicked(): Promise<void> {
    if (!this.exercise) {
      return;
    }

    this.busy = true;
    this.exercise.isDirty = false;

    try {
      this.checkMandatoryFields();

      await this.saveExercise();
      this.busy = false;

      const isContentOkay = await this.performContentCheck();
      if (!isContentOkay) return;

      this.$router.push(
        `/${this.parent}/${this.moduleId}/${this.LESSON_SLUG}/${this.lessonId}/${this.EXERCISE_SLUG}`
      );
    } catch (e) {
      this.busy = false;
      this.$bus.$emit(BusEvent.ERROR, 'Could not save exercise', e);
    }
  }

  public checkMandatoryFields() {
    for (const key of Object.keys(this.exercise.listening_content.items)) {
      const item = this.exercise.listening_content.items[key];

      // Check if sentence was chosen
      const sentence = item.sentence;
      if (sentence === undefined || sentence === '') {
        throw Error('Please select a sentence for both items.');
      }
    }

    if (this.isNew) {
      (this.$refs.item0 as FieldListeningItem).checkMandatoryFields();
      (this.$refs.item1 as FieldListeningItem).checkMandatoryFields();
    }

    // Check if correct index was chosen
    if (this.exercise.listening_content.correct_index === undefined) {
      throw Error('Please select the correct answer.');
    }
  }

  public async performContentCheck() {
    const data = await Exercise.getJson(this.exercise.id);
    const result = await checkContent(data);

    if (isEmpty(result.result)) {
      return true;
    }

    this.$bus.$emit(BusEvent.ERROR, 'Could not save exercise', {
      messageItems: result.result.map((item) => ({
        message: item.message,
        key: item.word || item.sentence
      }))
    });

    return false;
  }

  public async saveExercise() {
    const isNew = !this.exercise.id;
    let lessonLockManager: LockManager;

    if (isNew) {
      lessonLockManager = new LockManager(this.LESSON_SLUG, this.lessonId);
      await lessonLockManager.tryAcquireLock();

      // Upload exercise
      await this.exercise.save();

      // call it again to save media files of content
      await this.saveExercise();
    } else {
      if (!this.lockManager) {
        this.lockManager = new LockManager(
          this.EXERCISE_SLUG,
          this.exercise.id
        );
      }
      await this.tryToLock();

      this.exercise.listening_content.parent_exercise = this.exercise.id;
      await this.exercise.listening_content.save();

      await this.saveMediaFiles();

      // Upload items with media ids
      await this.exercise.listening_content.items[0].save();
      await this.exercise.listening_content.items[1].save();
      await this.exercise.save();
    }

    if (this.lockManager && !this.lockManager.isAvailable) {
      await this.lockManager.releaseLock();
    }
    if (lessonLockManager) {
      await lessonLockManager.releaseLock();
    }
  }

  public async saveMediaFiles(): Promise<void> {
    if (!this.exercise || !this.exercise.id) {
      throw Error('Exercise has not been created yet.');
    }

    this.imagesToDelete.forEach((id) => Media.deleteImage(id));
    this.imagesToDelete = [];

    this.audioToDelete.forEach((id) => Media.deleteAudio(id));
    this.audioToDelete = [];

    const promises: Array<PromiseLike<void>> = [
      (this.$refs.item0 as FieldListeningItem).saveMediaFiles(),
      (this.$refs.item1 as FieldListeningItem).saveMediaFiles()
    ];
    await Promise.all(promises);
  }

  public async jsonView(): Promise<void> {
    this.$router.push(`/json/${this.exercise.id}`);
  }
}
